import { useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useDisclosure } from "@mantine/hooks";
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import BackButton from "../../../../components/View/BackButton";
import { IconPencilDown } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useParams } from "react-router-dom";
import { updateDatafieldRows } from "../../../../api/ContactAPI";
import {
  gridFilterModelSelector,
  gridRowCountSelector,
  gridRowSelectionStateSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { transformFilterToServer } from "../../../../functions/utilFunctions";

const EditColumnRows = ({ current, total }) => {
  const { contactListId } = useParams();
  const lang = useLanguage((s) => s.language);
  const [opened, { open, close }] = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [overwriteType, setOverwriteType] = useState("ignore");

  const apiRef = useGridApiContext();
  const selectedIds = useGridSelector(apiRef, gridRowSelectionStateSelector);
  const filter = useGridSelector(apiRef, gridFilterModelSelector);
  const filterResultNumber = useGridSelector(apiRef, gridRowCountSelector);

  const handleOpen = (e) => {
    open();
    e.stopPropagation();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    setLoading(true);
    updateDatafieldRows({
      contact_list_id: parseInt(contactListId),
      datafield_id: current.id,
      overwrite: overwriteType === "overwrite",
      value: data.value,
      contacts_selection: {
        filter:
          data.target === "selection"
            ? { mode: "or", values: [] }
            : (filter?.items?.length > 0 || filter?.quickFilterValues?.[0]) &&
              data.target !== "selection"
            ? transformFilterToServer(filter, false)
            : { mode: "or", values: [] },
        excluded_contact_ids:
          data.target === "selection_inverse" ? selectedIds : [],
        contact_ids:
          data.target === "selection_inverse"
            ? []
            : data.target === "selection"
            ? selectedIds
            : [],
      },
    })
      .then((res) => {
        notifications.show({
          message: lang.prm.details.history.edited,
        });
        close();
        document.getElementById("list-content-table-reload").click();
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    close();
  };

  return (
    <>
      <Tooltip label={lang.contact_lists.rows_update.button_tooltip}>
        <ActionIcon
          className="hover-target"
          variant="subtle"
          color="tertiary.8"
          onClick={handleOpen}
        >
          <IconPencilDown size={18} />
        </ActionIcon>
      </Tooltip>

      {opened && (
        <Modal
          opened={opened}
          size={"lg"}
          onClose={handleClose}
          title={
            <>
              {lang.contact_lists.rows_update.title}
              <b>
                {" "}
                {current?.name} #{current.id}
              </b>
            </>
          }
          styles={{
            title: {
              lineHeight: 1.5,
            },
          }}
        >
          <Stack
            component={"form"}
            onSubmit={handleSubmit}
            gap={"xs"}
            pos={"relative"}
          >
            <LoadingOverlay visible={loading} />

            <Textarea
              label={lang.contact_lists.rows_update.value_label}
              placeholder={lang.contact_lists.rows_update.value_placeholder}
              name="value"
              rows={3}
              w={"100%"}
              maxLength={1000}
              required
              autoFocus
              data-autofocus
              size="md"
            />

            <Text>{lang.contact_lists.rows_update.type_text}</Text>
            <RadioGroup
              name="type"
              value={overwriteType}
              onChange={setOverwriteType}
            >
              <Stack>
                <Radio
                  size="md"
                  label={lang.contact_lists.rows_update.ignore_label}
                  value={"ignore"}
                />
                <Radio
                  size="md"
                  label={lang.contact_lists.rows_update.overwrite_label}
                  value={"overwrite"}
                  error={
                    overwriteType === "overwrite" &&
                    lang.contact_lists.rows_update.overwrite_error
                  }
                />
              </Stack>
            </RadioGroup>

            <Divider my={8} />

            <Text> {lang.contact_lists.rows_update.target_text} </Text>
            <RadioGroup name="target" required>
              <Stack>
                <Tooltip
                  label={lang.contact_lists.delete_contacts.no_filter}
                  disabled={filter?.items?.length > 0}
                >
                  <Radio
                    label={`${lang.contact_lists.export_contacts.filter} ${
                      filter?.items?.length > 0
                        ? `( ${filterResultNumber} )`
                        : ""
                    }`}
                    disabled={!filter || filter?.items?.length < 1}
                    value={"filter"}
                    name="target"
                    size="md"
                    required
                  />
                </Tooltip>

                <Tooltip
                  label={lang.contact_lists.delete_contacts.no_selection}
                  disabled={selectedIds?.length > 0}
                >
                  <Radio
                    label={`${lang.contact_lists.export_contacts.selection} ${
                      selectedIds?.length > 0 ? `( ${selectedIds.length} )` : ""
                    }`}
                    disabled={!selectedIds || selectedIds?.length < 1}
                    value={"selection"}
                    name="target"
                    required
                    size="md"
                  />
                </Tooltip>

                <Tooltip
                  label={lang.contact_lists.delete_contacts.no_selection}
                  disabled={selectedIds?.length > 0}
                >
                  <Radio
                    label={`${
                      lang.contact_lists.export_contacts.all_but_selection
                    } ${
                      selectedIds?.length > 0
                        ? `( ${filterResultNumber - selectedIds.length} )`
                        : ""
                    }`}
                    disabled={!selectedIds || selectedIds?.length < 1}
                    value={"selection_inverse"}
                    name="target"
                    required
                    size="md"
                  />
                </Tooltip>

                <Radio
                  label={`${lang.contact_lists.export_contacts.all_in_list} ${
                    total ? `(${total})` : ""
                  }`}
                  value={"export_all"}
                  name="target"
                  required
                  size="md"
                />
              </Stack>
            </RadioGroup>

            <Group justify="end" mt={"md"}>
              <BackButton onClick={handleClose} size="md" />

              <Button
                leftSection={<IconPencilDown size={18} />}
                loading={loading}
                color="primary"
                type="submit"
                size="md"
              >
                {lang.contact_lists.rows_update.submit_button}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </>
  );
};

export default EditColumnRows;
